import {useState, useEffect} from 'react';
import {SCREEN_SIZE} from '@constants/index';

interface KeyboardVisibilityState {
    isKeyboardVisible: boolean;
    keyboardHeight: number;
    viewportHeight: number;
    viewportOffsetTop: number;
}

const EDITABLE_ELEMENT_SELECTOR = 'input, textarea, [contenteditable=""], [contenteditable="true"]';

function getIsEditableElementFocused(): boolean {
    const {activeElement} = document;

    return Boolean(activeElement && activeElement.matches(EDITABLE_ELEMENT_SELECTOR));
}

/**
 * Hook to detect mobile keyboard visibility using the Visual Viewport API.
 * This is particularly important for iframe contexts where 100dvh doesn't
 * properly account for keyboard height.
 *
 * @returns Object containing keyboard visibility state and height
 */
export default function useKeyboardVisibility(): KeyboardVisibilityState {
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth <= SCREEN_SIZE.SM : false
    );
    const [state, setState] = useState<KeyboardVisibilityState>({
        isKeyboardVisible: false,
        keyboardHeight: 0,
        viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
        viewportOffsetTop: 0
    });

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        function handleResize() {
            setIsMobile(window.innerWidth <= SCREEN_SIZE.SM);
        }

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined' || !isMobile) {
            setState({
                isKeyboardVisible: false,
                keyboardHeight: 0,
                viewportHeight: typeof window !== 'undefined' ? window.innerHeight : 0,
                viewportOffsetTop: 0
            });
            return;
        }

        let maxViewportHeight = window.innerHeight;
        const keyboardThreshold = 150;

        function handleViewportChange() {
            const visualViewportHeight = window.visualViewport?.height ?? window.innerHeight;

            // Track max height to handle cases where initial capture was during transient state
            if (visualViewportHeight > maxViewportHeight) {
                maxViewportHeight = visualViewportHeight;
            }

            const heightDifference = maxViewportHeight - visualViewportHeight;
            // Collapsing browser chrome shrinks the viewport as much as a keyboard does, only a focused field tells them apart
            const isKeyboardVisible = getIsEditableElementFocused() && heightDifference > keyboardThreshold;

            setState({
                isKeyboardVisible,
                keyboardHeight: isKeyboardVisible ? heightDifference : 0,
                viewportHeight: visualViewportHeight,
                viewportOffsetTop: isKeyboardVisible ? window.visualViewport?.offsetTop ?? 0 : 0
            });
        }

        window.visualViewport?.addEventListener('resize', handleViewportChange);
        window.visualViewport?.addEventListener('scroll', handleViewportChange);
        document.addEventListener('focusin', handleViewportChange);
        document.addEventListener('focusout', handleViewportChange);

        handleViewportChange();

        return () => {
            window.visualViewport?.removeEventListener('resize', handleViewportChange);
            window.visualViewport?.removeEventListener('scroll', handleViewportChange);
            document.removeEventListener('focusin', handleViewportChange);
            document.removeEventListener('focusout', handleViewportChange);
        };
    }, [isMobile]);

    return state;
}
